/* Display verified YMQ bulk savings on the products that earned them.
 * YMQ creates an order-wide discount; Shopify otherwise spreads it onto hats
 * and unrelated products. Never change the cart, checkout, or tax calculation.
 */
(() => {
  'use strict';

  function tierPrices(data) {
    if (data.currency !== 'USD' || data.taxesIncluded || !data.rules) return null;
    const rules = JSON.parse(data.rules);
    const expected = new Map();
    const perItem = new Map();
    const unsupported = new Set();

    for (const [id, rule] of Object.entries(rules)) {
      const items = data.items.filter(item => item.group === id);
      if (!items.length) continue;
      const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
      const tier = rule.items.find(([min, max]) => quantity >= min && (max == null || quantity <= max));
      if (!tier) { unsupported.add(rule.name); continue; }
      const discount = tier[2];
      const dollars = Number(discount.value);
      // A group below its first discount tier earns no savings, regardless of
      // grouping mode. It must not disable attribution for unrelated products.
      if (dollars === 0) continue;
      // Verify each named discount independently. Unknown rules sharing a title
      // prevent attribution of that title, without disabling other verified rules.
      const productPercent = Number(discount.type) === 1 && Number(rule.gb) === 0 && dollars <= 100 &&
        [8256298615002, 8988548530394, 8455801372890].includes(items[0].product) &&
        items.every(item => item.product === items[0].product);
      const fixedProduct = Number(discount.type) === 2 && (
        (Number(rule.gb) === 2 && items.every(item => item.product === Number(rule.gid))) ||
        (Number(rule.gb) === 0 && [8256298615002, 8988548530394, 8455801372890].includes(items[0].product) &&
          items.every(item => item.product === items[0].product)));
      if (!Number.isFinite(dollars) || dollars < 0 || (!productPercent && !fixedProduct)) {
        unsupported.add(rule.name);
        continue;
      }
      const cents = Math.round(dollars * 100);
      for (const item of items) {
        const savings = Math.min(item.original, productPercent ? Math.round(item.original * dollars / 100) : cents * item.quantity);
        perItem.set(item.key, { title: rule.name, savings });
        expected.set(rule.name, (expected.get(rule.name) || 0) + savings);
      }
    }

    const verified = new Set();
    for (const [title, savings] of expected) {
      if (!savings || unsupported.has(title)) continue;
      const applications = data.applications.filter(app => app.title === title);
      if (applications.length !== 1) continue;
      const app = applications[0];
      const allocated = data.items.reduce((sum, item) => sum + item.allocations
        .filter(allocation => allocation.title === title)
        .reduce((amount, allocation) => amount + allocation.amount, 0), 0);
      if (app.target_selection !== 'all' ||
          app.value_type !== 'fixed_amount' || app.total_allocated_amount !== savings || allocated !== savings) continue;
      verified.add(title);
    }
    if (!verified.size) return null;

    const prices = data.items.map(item => {
      const own = perItem.get(item.key);
      const otherSavings = item.allocations.filter(allocation => !verified.has(allocation.title))
        .reduce((sum, allocation) => sum + allocation.amount, 0);
      const bulkSavings = own && verified.has(own.title) ? own.savings : 0;
      return { ...item, bulkSavings, savings: otherSavings + bulkSavings,
        net: item.original - otherSavings - bulkSavings };
    });
    // Fail safely if the app is mid-update or another discount prevents reconciliation.
    if (prices.some(item => !Number.isSafeInteger(item.net) || item.net < 0) ||
        prices.reduce((sum, item) => sum + item.net, 0) !== data.total) return null;
    return prices;
  }

  const processed = new WeakSet();
  function renderSnapshot(snapshot) {
    if (processed.has(snapshot)) return;
    processed.add(snapshot);
    try {
      const data = JSON.parse(snapshot.textContent);
      const prices = tierPrices(data);
      if (!prices) return;
      const scope = snapshot.parentElement;
      const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US',
        { style: 'currency', currency: data.currency }).format(cents / 100);
      const labels = data.labels;
      const make = (tag, className, text) => {
        const node = document.createElement(tag);
        node.className = className;
        node.textContent = text;
        return node;
      };
      for (const box of scope.querySelectorAll('[data-rbc-price-key]')) {
        const item = prices.find(price => price.key === box.dataset.rbcPriceKey);
        if (!item) continue;
        const unit = box.dataset.rbcPriceMode === 'unit';
        const nodes = [];
        if (item.savings > 0) {
          nodes.push(make('span', 'visually-hidden', labels.regular));
          nodes.push(make('s', 'rbc-cart-price__was', money(unit ? item.unit : item.original)));
        }
        nodes.push(make('strong', 'rbc-cart-price__now', money(unit ? Math.round(item.net / item.quantity) : item.net)));
        if (unit) nodes.push(make('span', 'rbc-cart-price__label', item.net % item.quantity ? labels.average : labels.each));
        if (item.savings > 0 && unit) {
          const saving = make('div', 'rbc-cart-price__saving', '');
          saving.append(make('strong', '', item.bulkSavings ? labels.bulk : labels.discount),
            make('span', '', labels.savings.replace('RBC_AMOUNT', money(item.savings))));
          nodes.push(saving);
        } else if (item.savings > 0) nodes.push(make('span', 'rbc-cart-price__label', labels.after));
        box.classList.toggle('rbc-cart-price--discounted', item.savings > 0);
        box.replaceChildren(...nodes);
      }
    } catch (_) {
      // Keep Shopify's server-rendered allocation if data is missing or unsupported.
    }
  }

  const selector = 'script[data-rbc-cart-pricing]';
  document.querySelectorAll(selector).forEach(renderSnapshot);
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.matches(selector)) renderSnapshot(node);
      node.querySelectorAll(selector).forEach(renderSnapshot);
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

